/**
 * kalkylator-core.js — Jolat Proff, åtgångsformler (Agent C).
 * Ren logik, ingen DOM. Laddas med defer. Konsumeras av produktsidans
 * åtgångskalkylator (Agent B), /pages/kalkylator och Fogvalsguiden.
 *
 * API (window.JolatKalk) — SPEC §6-B/§6-C:
 *
 *   mlNeeded(breddMm, djupMm, langdM, spillPct) -> ml
 *     Formel: bredd × djup × längd × (1 + spill/100).
 *     mm × mm × m = ml eftersom 1 mm² tvärsnitt × 1 m längd = 1 000 mm³ = 1 ml.
 *
 *   suggestDepth(breddMm) -> mm
 *     Proffsregel: bredd < 10 → djup = bredd, annars djup = bredd / 2.
 *     Resultatet clampas till 6–15 mm och avrundas till heltal.
 *
 *   cartridges(totalMl, volymMl) -> st
 *     Math.ceil(totalMl / volymMl); 0 vid ogiltiga indata.
 *
 *   backerRod(breddMm) -> { diameter, standard }
 *     diameter = bredd × 1,25 (råvärde i mm),
 *     standard = närmaste standarddimension av 6/10/13/15/20/25/30/40/50
 *     (vid lika avstånd väljs den större — bottningslisten ska sitta med press).
 *
 *   Hjälpare utöver SPEC-API:t (additiva, bryter inget):
 *   backerRodLength(langdM) -> m   (foglängd × 1,05)
 *   parseVolumeMl(text, densitetGPerMl) -> ml | null
 *     Regex enligt SPEC §1: /(\d+(?:[.,]\d+)?)\s*(ml|l|kg|g)\b/i.
 *     kg/g räknas om till ml via densitet (fogspec.densitet_g_per_ml),
 *     fallback-densitet 1.3 g/ml.
 *
 * Enhetstest (SPEC §6-C-exemplet, verifierat för hand):
 *   mlNeeded(10, 5, 12, 10):  10 × 5 × 12 = 600 ml; × 1,10 = 660 ml  ✓
 *   cartridges(660, 310):     Math.ceil(660 / 310) = Math.ceil(2,129…) = 3  ✓
 *   → 10 mm × 5 mm × 12 m med 10 % spill kräver 3 st 310 ml-patroner.
 *   suggestDepth(12):         12 / 2 = 6 → 6 mm (inom 6–15)  ✓
 *   backerRod(12):            12 × 1,25 = 15 → { diameter: 15, standard: 15 }  ✓
 */
(function () {
  'use strict';

  var STANDARD_RODS = [6, 10, 13, 15, 20, 25, 30, 40, 50];

  function toNumber(value) {
    if (typeof value === 'number') return isFinite(value) ? value : 0;
    if (value == null) return 0;
    var n = parseFloat(String(value).replace(',', '.'));
    return isNaN(n) ? 0 : n;
  }

  var JolatKalk = {
    mlNeeded: function (breddMm, djupMm, langdM, spillPct) {
      var b = toNumber(breddMm);
      var d = toNumber(djupMm);
      var l = toNumber(langdM);
      var spill = Math.max(0, toNumber(spillPct));
      if (b <= 0 || d <= 0 || l <= 0) return 0;
      return b * d * l * (1 + spill / 100);
    },

    suggestDepth: function (breddMm) {
      var b = toNumber(breddMm);
      if (b <= 0) return 0;
      var d = b < 10 ? b : b / 2;
      return Math.min(15, Math.max(6, Math.round(d)));
    },

    cartridges: function (totalMl, volymMl) {
      var total = toNumber(totalMl);
      var vol = toNumber(volymMl);
      if (total <= 0 || vol <= 0) return 0;
      return Math.ceil(total / vol);
    },

    backerRod: function (breddMm) {
      var b = toNumber(breddMm);
      if (b <= 0) return { diameter: 0, standard: 0 };
      var raw = Math.round(b * 1.25 * 100) / 100;
      var standard = STANDARD_RODS[0];
      var bestDiff = Infinity;
      for (var i = 0; i < STANDARD_RODS.length; i++) {
        var diff = Math.abs(STANDARD_RODS[i] - raw);
        if (diff < bestDiff || (diff === bestDiff && STANDARD_RODS[i] > standard)) {
          bestDiff = diff;
          standard = STANDARD_RODS[i];
        }
      }
      return { diameter: raw, standard: standard };
    },

    backerRodLength: function (langdM) {
      var l = toNumber(langdM);
      return l > 0 ? l * 1.05 : 0;
    },

    parseVolumeMl: function (text, densitetGPerMl) {
      if (!text) return null;
      var m = String(text).match(/(\d+(?:[.,]\d+)?)\s*(ml|l|kg|g)\b/i);
      if (!m) return null;
      var value = parseFloat(m[1].replace(',', '.'));
      if (isNaN(value) || value <= 0) return null;
      var unit = m[2].toLowerCase();
      var densitet = toNumber(densitetGPerMl);
      if (densitet <= 0) densitet = 1.3;
      if (unit === 'ml') return value;
      if (unit === 'l') return value * 1000;
      if (unit === 'kg') return (value * 1000) / densitet;
      if (unit === 'g') return value / densitet;
      return null;
    }
  };

  window.JolatKalk = JolatKalk;
})();
